/** @jsxImportSource @emotion/react */
import React, { useState } from 'react'
import { css } from '@emotion/react'
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import {
  font,
  weightMedium,
  weightSemiBold,
  paragraphSmall,
  paragraphXSmall,
} from 'src/components/css'
import type {
  ConstraintReportGroup,
  ConstraintReportStatus,
  ConstraintRuleEntry,
  ViolationEntry,
} from '../utils/constraintReport'
import { severityLabel } from '../utils/violations'

interface ConstraintReportPanelProps {
  groups: ConstraintReportGroup[]
  /**
   * Jump to a game row in the table (view/filter aware, handled by parent).
   * `divisionId` disambiguates a duplicate `game_number` (unique only within a
   * division) so the parent lands on the exact offending game.
   */
  onJumpToGame?: (gameNumber: number, divisionId: string | null) => void
}

// ─── Icons (lifted from the retired ViolationsSection) ───────────────────────

const ChevronIcon: React.FC<{ expanded: boolean; color?: string }> = ({
  expanded,
  color = colors.NEUTRAL_50,
}) => {
  const iconStyle = {
    transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
    transition: 'transform 0.2s ease',
    flexShrink: 0,
  }
  return (
    <svg
      width={18}
      height={18}
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      style={iconStyle}
    >
      <path
        d='M7.41 8.295L12 12.875L16.59 8.295L18 9.705L12 15.705L6 9.705L7.41 8.295Z'
        fill={color}
      />
    </svg>
  )
}

const ErrorIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
    <path
      d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM11 7H13V13H11V7ZM11 15H13V17H11V15Z'
      fill={colors.ERROR_200}
    />
  </svg>
)

const WarningIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
    <path
      d='M12 5.99L19.53 19H4.47L12 5.99ZM12 2L1 21H23L12 2ZM13 16H11V18H13V16ZM13 10H11V14H13V10Z'
      fill={colors.WARNING_300}
    />
  </svg>
)

const CheckIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
    <path
      d='M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z'
      fill={colors.SUCCESS_300}
    />
  </svg>
)

const DisabledIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
    <path d='M19 13H5v-2h14v2z' fill={colors.NEUTRAL_70} />
  </svg>
)

// Info "i" circle for the section header explainer tooltip.
const InfoIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 16,
  color = colors.NEUTRAL_50,
}) => (
  <svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
    <path
      d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM11 7H13V9H11V7ZM11 11H13V17H11V11Z'
      fill={color}
    />
  </svg>
)

const StatusIcon: React.FC<{ status: ConstraintReportStatus }> = ({
  status,
}) => {
  if (status === 'conflict' || status === 'setup_impossible') {
    return <ErrorIcon />
  }
  if (status === 'advisory') {
    return <WarningIcon />
  }
  if (status === 'limited_by_setup') {
    // A forced shortfall is informational, not a penalty — calm "i", not a ✗.
    return <InfoIcon size={18} color={colors.INFO_300} />
  }
  if (status === 'disabled') {
    return <DisabledIcon />
  }
  return <CheckIcon />
}

/** Readable constraint-type name for the "Limited by setup" blocker chips. */
const humanizeConstraintCode = (code: string): string =>
  code
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

// ─── Styles ──────────────────────────────────────────────────────────────────

const containerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  margin-top: 24px;
`

const headerRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const sectionTitleStyles = css`
  ${font.body};
  ${weightSemiBold};
  font-size: 14px;
  line-height: 16.8px;
  letter-spacing: 1.68px;
  text-transform: uppercase;
  color: ${colors.WHITE};
  margin: 0;
`

const titleGroupStyles = css`
  display: flex;
  align-items: center;
  gap: 6px;
`

const infoIconStyles = css`
  display: inline-flex;
  align-items: center;
  cursor: help;

  svg path {
    transition: fill 0.15s ease;
  }
  &:hover svg path,
  &:focus-visible svg path {
    fill: ${colors.NEUTRAL_30};
  }
  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: 2px;
    border-radius: 50%;
  }
`

const summaryStyles = (tone: 'attention' | 'calm') => css`
  ${paragraphXSmall};
  ${weightSemiBold};
  color: ${tone === 'attention' ? colors.ERROR_100 : colors.NEUTRAL_70};
  margin: 0;
`

const tableContainerStyles = css`
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
`

const groupHeaderStyles = (expanded: boolean) => css`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  cursor: pointer;
  background: ${expanded ? colors.SECONDARY_300 : 'transparent'};
  border-bottom: 1px solid ${colors.SECONDARY_200};
  transition: background 0.15s ease;

  &:hover {
    background: ${colors.SECONDARY_300};
  }
`

const groupNameStyles = css`
  ${paragraphSmall};
  ${weightSemiBold};
  color: ${colors.WHITE};
  margin: 0;
`

const categoryChipStyles = css`
  ${paragraphXSmall};
  ${weightMedium};
  color: ${colors.NEUTRAL_70};
  background: ${colors.SECONDARY_200};
  border-radius: 4px;
  padding: 1px 6px;
  white-space: nowrap;
`

const statusLabelColor = (status: ConstraintReportStatus): string => {
  if (status === 'conflict' || status === 'setup_impossible') {
    return colors.ERROR_200
  }
  if (status === 'advisory') {
    return colors.WARNING_300
  }
  if (status === 'limited_by_setup') {
    return colors.INFO_300
  }
  if (status === 'disabled') {
    return colors.NEUTRAL_70
  }
  return colors.SUCCESS_100
}

const statusLabelStyles = (status: ConstraintReportStatus) => css`
  ${paragraphXSmall};
  ${weightSemiBold};
  margin: 0;
  white-space: nowrap;
  color: ${statusLabelColor(status)};
`

const rulesContainerStyles = css`
  display: flex;
  flex-direction: column;
  background: ${colors.SECONDARY_500};
  border-bottom: 1px solid ${colors.SECONDARY_200};
`

const ruleHeaderStyles = css`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 16px 6px 38px;
`

const ruleSummaryStyles = css`
  ${paragraphXSmall};
  ${weightMedium};
  color: ${colors.NEUTRAL_30};
  margin: 0;
`

const ruleScopeStyles = css`
  ${paragraphXSmall};
  color: ${colors.NEUTRAL_70};
  margin: 0;
`

const tagStyles = (kind: 'hard' | 'soft') => css`
  ${paragraphXSmall};
  ${weightSemiBold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 4px;
  padding: 0 5px;
  color: ${kind === 'hard' ? colors.ERROR_100 : colors.NEUTRAL_70};
  background: ${kind === 'hard'
    ? `${colors.ERROR_200}1f`
    : colors.SECONDARY_200};
`

const entryRowStyles = (severity: ViolationEntry['severity']) => css`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 6px 16px 8px 38px;
  border-left: 3px solid
    ${severity === 'error'
      ? colors.ERROR_200
      : severity === 'warning'
      ? colors.WARNING_300
      : colors.INFO_300};
`

const severityBadgeStyles = (severity: ViolationEntry['severity']) => css`
  ${paragraphXSmall};
  ${weightSemiBold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 4px;
  padding: 1px 6px;
  flex-shrink: 0;
  margin-top: 1px;
  color: ${severity === 'error'
    ? colors.ERROR_200
    : severity === 'warning'
    ? colors.WARNING_300
    : colors.INFO_300};
  background: ${severity === 'error'
    ? `${colors.ERROR_200}20`
    : severity === 'warning'
    ? `${colors.WARNING_300}20`
    : `${colors.INFO_300}20`};
`

const entryBodyStyles = css`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`

const entryHeadlineStyles = css`
  ${paragraphXSmall};
  ${weightSemiBold};
  color: ${colors.WHITE};
  margin: 0;
`

const entryMessageStyles = css`
  ${paragraphXSmall};
  color: ${colors.NEUTRAL_50};
  margin: 0;
`

const entrySuggestionStyles = css`
  ${paragraphXSmall};
  color: ${colors.NEUTRAL_70};
  font-style: italic;
  margin: 0;
`

const blockedByStyles = css`
  ${paragraphXSmall};
  color: ${colors.INFO_300};
  margin: 0;
`

const chipRowStyles = css`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
`

const gameChipStyles = css`
  ${paragraphXSmall};
  ${weightMedium};
  color: ${colors.PRIMARY};
  background: transparent;
  border: 1px solid ${colors.PRIMARY};
  border-radius: 10px;
  padding: 0 8px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${colors.PRIMARY}1f;
  }
`

const preventedTagStyles = css`
  ${paragraphXSmall};
  color: ${colors.NEUTRAL_70};
  font-style: italic;
  margin-left: 6px;
`

// ─── Helpers ─────────────────────────────────────────────────────────────────

const groupStatusLabel = (g: ConstraintReportGroup): string => {
  if (g.status === 'conflict') {
    return `${g.rollup.conflicts} conflict${
      g.rollup.conflicts === 1 ? '' : 's'
    }`
  }
  if (g.status === 'setup_impossible') {
    return 'Setup impossible'
  }
  if (g.status === 'advisory') {
    return `${g.rollup.advisories} violation${
      g.rollup.advisories === 1 ? '' : 's'
    }`
  }
  if (g.status === 'limited_by_setup') {
    return 'Limited by setup'
  }
  if (g.status === 'disabled') {
    return 'Disabled'
  }
  return '✓ OK'
}

const ruleStatusLabel = (r: ConstraintRuleEntry): string => {
  if (r.status === 'disabled') {
    return 'Disabled'
  }
  if (r.status === 'ok') {
    return '✓ OK'
  }
  if (r.status === 'setup_impossible') {
    return 'Setup impossible'
  }
  if (r.status === 'limited_by_setup') {
    return 'Limited by setup'
  }
  return severityLabel(r.status === 'conflict' ? 'error' : 'warning')
}

/** Entry-level label: a forced shortfall reads "Limited by setup", not "Info". */
const entryBadgeLabel = (e: ViolationEntry): string => {
  if (e.outcome === 'forced_shortfall') {
    return 'Limited by setup'
  }
  if (
    e.outcome === 'setup_impossible' ||
    e.outcome === 'infeasible_configuration'
  ) {
    return 'Setup impossible'
  }
  return severityLabel(e.severity)
}

/** "9 / 3" target read-out when the solver gave us actual + target. */
const actualTargetText = (e: ViolationEntry): string | null => {
  if (e.actual != null && e.target != null) {
    return `${e.actual} / ${e.target}`
  }
  if (e.actual != null) {
    return String(e.actual)
  }
  return null
}

const entryHeadline = (e: ViolationEntry): string => {
  const parts: string[] = []
  if (e.teamLabel) {
    parts.push(e.teamLabel)
  }
  if (e.scopeLabel) {
    parts.push(e.scopeLabel)
  }
  const at = actualTargetText(e)
  if (at) {
    parts.push(at)
  }
  return parts.join(' · ')
}

type GameRef = ViolationEntry['gameRefs'][number]

// Dedupe by the per-division gameNumber (the jump key); order by the displayed
// flight-unique number.
const uniqueGameRefs = (refs: GameRef[]): GameRef[] => {
  const byNumber = new Map<number, GameRef>()
  for (const r of refs) {
    if (!byNumber.has(r.gameNumber)) {
      byNumber.set(r.gameNumber, r)
    }
  }
  return Array.from(byNumber.values()).sort(
    (a, b) => a.displayNumber - b.displayNumber
  )
}

// ─── Entry / rule rows ─────────────────────────────────────────────────────────

const EntryRow: React.FC<{
  entry: ViolationEntry
  onJumpToGame?: (gameNumber: number, divisionId: string | null) => void
}> = ({ entry, onJumpToGame }) => {
  const headline = entryHeadline(entry)
  const games = uniqueGameRefs(entry.gameRefs)
  return (
    <div css={entryRowStyles(entry.severity)}>
      <span css={severityBadgeStyles(entry.severity)}>
        {entryBadgeLabel(entry)}
      </span>
      <div css={entryBodyStyles}>
        {headline && (
          <p css={entryHeadlineStyles}>
            {headline}
            {entry.isPrevented && (
              <span css={preventedTagStyles}>auto-resolved</span>
            )}
          </p>
        )}
        <p css={entryMessageStyles}>{entry.message}</p>
        {entry.blockedBy.length > 0 && (
          <p css={blockedByStyles}>
            Limited by:{' '}
            {entry.blockedBy
              .map((b) => humanizeConstraintCode(b.constraintType))
              .join(', ')}
          </p>
        )}
        {entry.suggestion && (
          <p css={entrySuggestionStyles}>{entry.suggestion}</p>
        )}
        {onJumpToGame && games.length > 0 && (
          <div css={chipRowStyles}>
            {games.map((g) => (
              <Tooltip
                key={g.gameNumber}
                title='Jump to this game in the schedule'
                placement='top'
              >
                <button
                  type='button'
                  css={gameChipStyles}
                  onClick={() => onJumpToGame(g.gameNumber, entry.divisionId)}
                >
                  Game {g.displayNumber}
                </button>
              </Tooltip>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const RuleBlock: React.FC<{
  rule: ConstraintRuleEntry
  fallbackName: string
  onJumpToGame?: (gameNumber: number, divisionId: string | null) => void
}> = ({ rule, fallbackName, onJumpToGame }) => (
  <div>
    <div css={ruleHeaderStyles}>
      <p css={ruleSummaryStyles}>{rule.paramSummary ?? fallbackName}</p>
      {rule.scopeLabel && <p css={ruleScopeStyles}>· {rule.scopeLabel}</p>}
      <span css={tagStyles(rule.isHard ? 'hard' : 'soft')}>
        {rule.isHard ? 'Required' : 'Preference'}
      </span>
      <span css={statusLabelStyles(rule.status)}>{ruleStatusLabel(rule)}</span>
    </div>
    {rule.violations.map((entry, i) => (
      <EntryRow key={i} entry={entry} onJumpToGame={onJumpToGame} />
    ))}
  </div>
)

// ─── Panel ───────────────────────────────────────────────────────────────────

// Wider than antd's 250px default so the explainer reads in fewer lines.
const infoTooltipOverlayStyle = { maxWidth: 340 }

// Plain-language explainer surfaced by the header info icon.
const sectionInfoTooltip = (
  <span>
    <strong>Violations</strong> come from <em>soft</em> constraints
    (preferences) it couldn’t optimize any further — they’re optional, so your
    schedule still works.
    <br />
    <br />
    <strong>Conflicts</strong> come from <em>hard</em> constraints
    (requirements) it couldn’t meet — fix these before publishing.
    <br />
    <br />
    Click a game (e.g. Game 7) to jump to it in the schedule.
  </span>
)

const ConstraintReportPanel: React.FC<ConstraintReportPanelProps> = ({
  groups,
  onJumpToGame,
}) => {
  'use memo'

  // Collapsed by default — group headers still surface each rule's status +
  // counts at a glance; the user expands a group to see its violations.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())

  // Surface constraints that tripped something OR were limited by setup —
  // satisfied (✓ OK) and disabled rules are hidden. Forced shortfalls
  // (limited_by_setup) and setup_impossible show calmly, NOT as conflicts.
  const visibleGroups = groups.filter(
    (g) =>
      g.status === 'conflict' ||
      g.status === 'advisory' ||
      g.status === 'limited_by_setup' ||
      g.status === 'setup_impossible'
  )

  if (visibleGroups.length === 0) {
    return null
  }

  const totals = visibleGroups.reduce(
    (acc, g) => ({
      conflicts: acc.conflicts + g.rollup.conflicts,
      advisories: acc.advisories + g.rollup.advisories,
    }),
    { conflicts: 0, advisories: 0 }
  )
  const impossibleCount = visibleGroups.filter(
    (g) => g.status === 'setup_impossible'
  ).length
  const limitedCount = visibleGroups.filter(
    (g) => g.status === 'limited_by_setup'
  ).length

  const summaryParts: string[] = []
  if (totals.conflicts > 0) {
    summaryParts.push(
      `${totals.conflicts} conflict${totals.conflicts === 1 ? '' : 's'}`
    )
  }
  if (impossibleCount > 0) {
    summaryParts.push(
      `${impossibleCount} setup issue${impossibleCount === 1 ? '' : 's'}`
    )
  }
  if (totals.advisories > 0) {
    summaryParts.push(
      `${totals.advisories} violation${totals.advisories === 1 ? '' : 's'}`
    )
  }
  if (limitedCount > 0) {
    summaryParts.push(`${limitedCount} limited by setup`)
  }
  const summaryText =
    summaryParts.length > 0
      ? summaryParts.join(' · ')
      : 'All defined constraints satisfied'
  const summaryTone =
    totals.conflicts > 0 || impossibleCount > 0 ? 'attention' : 'calm'

  const toggle = (code: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(code)) {
        next.delete(code)
      } else {
        next.add(code)
      }
      return next
    })

  return (
    <div css={containerStyles}>
      <div css={headerRowStyles}>
        <div css={titleGroupStyles}>
          <p css={sectionTitleStyles}>Constraints &amp; Violations</p>
          <Tooltip
            title={sectionInfoTooltip}
            placement='top'
            overlayStyle={infoTooltipOverlayStyle}
          >
            <span
              css={infoIconStyles}
              role='img'
              aria-label='About this section'
              tabIndex={0}
            >
              <InfoIcon size={16} />
            </span>
          </Tooltip>
        </div>
        <p css={summaryStyles(summaryTone)}>{summaryText}</p>
      </div>

      <div css={tableContainerStyles}>
        {visibleGroups.map((group) => {
          const isOpen = expanded.has(group.code)
          return (
            <div key={group.code}>
              <div
                css={groupHeaderStyles(isOpen)}
                onClick={() => toggle(group.code)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    toggle(group.code)
                  }
                }}
              >
                <ChevronIcon expanded={isOpen} />
                <StatusIcon status={group.status} />
                <p css={groupNameStyles}>{group.displayName}</p>
                <span css={categoryChipStyles}>{group.category}</span>
                <span
                  css={css`
                    flex: 1;
                  `}
                />
                <p css={statusLabelStyles(group.status)}>
                  {groupStatusLabel(group)}
                </p>
              </div>

              {isOpen && (
                <div css={rulesContainerStyles}>
                  {group.rules.map((rule) => (
                    <RuleBlock
                      key={rule.id}
                      rule={rule}
                      fallbackName={group.displayName}
                      onJumpToGame={onJumpToGame}
                    />
                  ))}
                  {group.orphanViolations.length > 0 && (
                    <div>
                      <div css={ruleHeaderStyles}>
                        <p css={ruleSummaryStyles}>{group.displayName}</p>
                        <span css={tagStyles('soft')}>Unmatched</span>
                      </div>
                      {group.orphanViolations.map((entry, i) => (
                        <EntryRow
                          key={i}
                          entry={entry}
                          onJumpToGame={onJumpToGame}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ConstraintReportPanel
